import { createApp } from 'vue';

import CamCalculator from './components/CamCalculator.vue';
import { getBandForTeamSize } from './calculations.js';
import store from './store.js';

/**
 * Resolve the origin of the framing parent window from document.referrer so
 * resize messages are only posted to the embedding origin.
 *
 * @returns {string | null} Parent origin URL or null when unavailable.
 */
function getParentOrigin() {
	if (!document.referrer) {
		return null;
	}

	try {
		return new URL(document.referrer).origin;
	} catch {
		return null;
	}
}

/**
 * Pre-seed the selected team size band from a `?team=<count>` query parameter
 * when present and valid.
 *
 * @returns {boolean} True when a valid team head count was applied to the store.
 */
function seedTeamSizeFromQuery() {
	const rawTeam = new URLSearchParams(window.location.search).get('team');
	if (!rawTeam) {
		return false;
	}

	const count = Number.parseInt(rawTeam, 10);
	if (!Number.isInteger(count) || count < 1) {
		return false;
	}

	store.setBand(getBandForTeamSize(count).id);
	return true;
}

const mountElement = document.querySelector('#cam-calculator');

if (mountElement) {
	const context = mountElement.dataset.context || 'page';
	const ctaHref = mountElement.dataset.ctaHref || '/agent-walkthrough/';
	const hasSeededTeamSize = context === 'embed' && seedTeamSizeFromQuery();

	const app = createApp(CamCalculator, {
		context,
		ctaHref,
		hasSeededTeamSize,
	});

	app.config.globalProperties.$store = store;
	app.mount(mountElement);

	const parentOrigin = getParentOrigin();
	if (context === 'embed' && parentOrigin && window.parent !== window) {
		if (typeof ResizeObserver !== 'undefined') {
			const postHeight = () => {
				const height = Math.ceil(document.body.offsetHeight);
				window.parent.postMessage(
					{ type: 'cam-calculator:resize', height },
					parentOrigin,
				);
			};

			const observer = new ResizeObserver(postHeight);
			observer.observe(mountElement);
			postHeight();
		}

		let interactionReported = false;
		const interactionEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
		const reportInteraction = () => {
			if (interactionReported) {
				return;
			}

			interactionReported = true;
			for (const eventName of interactionEvents) {
				window.removeEventListener(eventName, reportInteraction);
			}

			window.parent.postMessage(
				{ type: 'cam-calculator:interaction' },
				parentOrigin,
			);
		};

		for (const eventName of interactionEvents) {
			window.addEventListener(eventName, reportInteraction, { passive: true });
		}
	}
}
